import './App.css';

import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { OptimizelyProvider } from '@optimizely/react-sdk';
import { Toaster } from 'sonner';
import ReactTooltip from 'react-tooltip';

import getOptimizelyInstance, { getApplication, getApplicationVersion } from 'js/shared/helpers/optimizely';
import LoadingSpinner from 'js/shared/components/LoadingSpinner';
import Callout from 'js/shared/components/Callout';

import AuthenticatedComponent from './shared/components/AuthenticatedComponent';
import { validateToken, setAccountManagementRedirect } from './actionCreators/authActions';
import { ROUTE_CODES } from './config/routes';
import config from './config/config';
import { APP_TOOLTIP_ID } from './config/consts';
import { NoOnlineStorage } from './shared/components/NoOnlineStorage';
import { rendererLog } from './shared/lib/CloudRenderer';
import DownloadIframe from './shared/components/DownloadIframe';
import AcceptTermsAndMarketing from './shared/components/AcceptTermsAndMarketing';
import OfflineWarning from './shared/components/OfflineWarning';
import { getUID } from './shared/helpers/tokenHelpers';
import { changeNetworkStatus } from './actionCreators/networkActions';
import { useAppDispatch } from './store/hooks';
import { useAuthState } from './shared/hooks/useAuthState';
import { useUiState } from './shared/hooks/useUiState';
import { useCheckoutState } from './shared/hooks/useCheckoutState';

const HomeView = lazy(() => import('./home/HomeView'));
const AuthenticationRouter = lazy(() => import('./authentication/AuthenticationRouter'));
const RendererView = lazy(() => import('./renderer/RendererView'));
const EditorView = lazy(() => import('./editor/EditorView'));
const PreviewView = lazy(() => import('./preview/PreviewView'));

const { ENVIRONMENT } = config;

const optimizely = getOptimizelyInstance();

const App = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticating, currentUser, noOnlineStorage } = useAuthState();
  const { step: checkoutStep } = useCheckoutState();
  const checkingUpgrade = checkoutStep === 'CHECKING_UPGRADE';
  const { isCheckingTermsAcceptance, needsToAcceptTerms } = useUiState();
  const history = useHistory();

  const { id, accountType, email } = currentUser;
  const cookieUserId = getUID();
  const reduxUserId = id?.toString();
  const isRenderPath = history.location.pathname.includes('/render');

  useEffect(() => {
    dispatch(validateToken({ showSpinner: true, revalidation: false }));
  }, [dispatch]);

  useEffect(() => {
    const handleOffline = () => {
      dispatch(changeNetworkStatus(false));
    };

    const handleOnline = () => {
      dispatch(changeNetworkStatus(true));
    };

    if (!isRenderPath) {
      // Only load these dependencies for the main app and not the renderer to try and speed things up a little on the renderer.
      window.addEventListener('offline', handleOffline);
      window.addEventListener('online', handleOnline);

      if (window.location.search.indexOf('referrer=account') !== -1) {
        dispatch(setAccountManagementRedirect(true));
      }

      return () => {
        window.removeEventListener('offline', handleOffline);
        window.removeEventListener('online', handleOnline);
      };
    }
  }, [dispatch, isRenderPath]);

  useEffect(() => {
    if (!isRenderPath) {
      const onWindowFocus = () => {
        // Compare user IDs and refresh if different
        if (cookieUserId && reduxUserId && cookieUserId !== reduxUserId) {
          rendererLog('User ID changed, refreshing...');
          window.location.reload();
        }
      };

      window.addEventListener('focus', onWindowFocus);

      return () => {
        window.removeEventListener('focus', onWindowFocus);
      };
    }
  }, [cookieUserId, isRenderPath, reduxUserId]);

  useEffect(() => {
    if (!isRenderPath) {
      const onSleekNoteLoad = (event: {
        data: { type: string; command: (cmd: string, campaignId: string) => void; campaignId: string };
      }) => {
        if (event.data.type === 'load')
          window.sleekNoteClose = () => event.data.command('close', event.data.campaignId);
      };
      document.addEventListener('sleekNote', onSleekNoteLoad as unknown as EventListener);

      return () => {
        document.removeEventListener('sleekNote', onSleekNoteLoad as unknown as EventListener);
      };
    }
  }, [isRenderPath]);

  if (isAuthenticating) {
    return <LoadingSpinner />;
  }

  ReactTooltip.rebuild();

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        id: id || '',
        attributes: {
          userTier: accountType,
          env: ENVIRONMENT,
          email: email || '',
          userId: id || '',
          application: getApplication(),
          applicationVersion: getApplicationVersion()
        }
      }}
    >
      <div className="App">
        <Callout />
        <Suspense fallback={<LoadingSpinner />}>
          <Switch>
            <Route path={ROUTE_CODES.RENDER} component={RendererView} />

            <Route path={ROUTE_CODES.PREVIEW} component={PreviewView} />

            <Route path={ROUTE_CODES.AUTH} component={AuthenticationRouter} />

            {isCheckingTermsAcceptance && <LoadingSpinner />}

            {needsToAcceptTerms && <AcceptTermsAndMarketing />}

            {noOnlineStorage && <NoOnlineStorage />}

            <AuthenticatedComponent path={ROUTE_CODES.EDITOR} component={EditorView} />
            <AuthenticatedComponent path={ROUTE_CODES.HOME} component={HomeView} />
          </Switch>
          <ReactTooltip id={APP_TOOLTIP_ID} className="t-utility-label" />

          <DownloadIframe />
        </Suspense>

        {checkingUpgrade && <LoadingSpinner message="Upgrading your account" />}
        <Toaster position="bottom-left" />
        <OfflineWarning />
      </div>
    </OptimizelyProvider>
  );
};

export default App;
